import React, { useState, useEffect } from 'react';
import { Search, Filter, FileText, Printer, Package, Truck, CheckCircle, Clock, AlertCircle, RefreshCw, Loader, ClipboardList, Copy, Check, MapPin, User, Store, CalendarClock } from 'lucide-react';
// Package ainda usado no estado vazio da tabela
import clsx from 'clsx';
import apiClient from '../services/api/client';

// A estrutura padrão de Pedidos do QFlow (Normalizada)
interface OrderItem {
  sku: string;
  title: string;
  quantity: number;
  unitPrice: number;
}

interface OrderInvoice {
  key: string;
  number: string;
  series?: string;
  issuedAt?: string;
  amount?: number;
  issuerCnpj?: string;
  authorized?: boolean;
  status: 'enviando' | 'enviada' | 'aprovada' | 'erro';
  message?: string;
  uploadedAt?: string;
}

interface NfeHistory {
  id: string; key: string; number: string; series?: string; amount?: number; issuedAt?: string;
  ativa: boolean; status?: string; mensagem?: string; enviadoPor?: string; criadoEm?: string;
  tentativas: { em: string; origem: string; status?: string; mensagem?: string }[];
}

type UiStatus = 'pending' | 'invoiced' | 'ready_to_ship' | 'shipped' | 'delivered' | 'cancelled';

interface Order {
  _id: string;
  orderId: string; // ID original do Marketplace
  marketplace: string;
  customerName: string;
  customerDocument?: string;
  totalValue: number;
  status: UiStatus;
  rawStatus: string;
  createdAt: string;
  items: OrderItem[];
  invoice?: OrderInvoice;
  shipBy?: string; // prazo de postagem (YYYY-MM-DD)
  shipping?: {
    method: string;
    trackingCode?: string;
  };
}

/** Converte o status cru de cada marketplace no status do QFlow */
function toUiStatus(raw: string, invoice?: OrderInvoice): UiStatus {
  const s = (raw || '').toLowerCase();
  if (['cancelled', 'canceled', 'cancelado', 'in_cancel', 'to_return'].includes(s)) return 'cancelled';
  if (['delivered', 'completed', 'finished', 'entregue'].includes(s)) return 'delivered';
  if (['shipped', 'in_transit', 'shipping', 'enviado'].includes(s)) return 'shipped';
  if (invoice && (invoice.status === 'enviada' || invoice.status === 'aprovada')) return 'invoiced';
  if (['invoiced', 'faturado'].includes(s)) return 'invoiced';
  if (['ready_to_ship', 'processed', 'retry_ship'].includes(s)) return 'ready_to_ship';
  return 'pending'; // new, approved, paid, ready_to_ship sem NF etc.
}

const INVOICE_BADGE: Record<OrderInvoice['status'], { label: string; cls: string }> = {
  enviando: { label: 'Enviando ao marketplace…', cls: 'text-amber-400' },
  enviada:  { label: 'Enviada — em validação', cls: 'text-blue-400' },
  aprovada: { label: 'Aprovada', cls: 'text-emerald-400' },
  erro:     { label: 'Recusada', cls: 'text-red-400' },
};

/** Lê os dados principais do XML da NF-e no navegador (só para pré-visualização) */
function previewNfe(xml: string) {
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) return null;
    const get = (tag: string, parent: Document | Element = doc) =>
      parent.getElementsByTagNameNS('*', tag)[0]?.textContent?.trim() || '';
    const inf = doc.getElementsByTagNameNS('*', 'infNFe')[0];
    if (!inf) return null;
    const key = (inf.getAttribute('Id') || '').replace(/\D/g, '') || get('chNFe').replace(/\D/g, '');
    const emit = doc.getElementsByTagNameNS('*', 'emit')[0];
    const dest = doc.getElementsByTagNameNS('*', 'dest')[0];
    return {
      key,
      number: get('nNF'),
      series: get('serie'),
      issuedAt: get('dhEmi') || get('dEmi'),
      amount: parseFloat(get('vNF') || '0'),
      emitente: emit ? get('xNome', emit) : '',
      destinatario: dest ? get('xNome', dest) : '',
      authorized: ['100', '150'].includes(get('cStat', doc.getElementsByTagNameNS('*', 'protNFe')[0] || doc)),
    };
  } catch {
    return null;
  }
}

const brl = (v: number) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MARKETPLACES_INFO: Record<string, { name: string; color: string; logo: string }> = {
  ml: { name: 'Mercado Livre', color: '#FFE600', logo: '🛒' },
  shopee: { name: 'Shopee', color: '#EE4D2D', logo: '🛍️' },
  magalu: { name: 'Magalu', color: '#0086FF', logo: '🛍️' },
  amazon: { name: 'Amazon', color: '#FF9900', logo: '📦' },
  casas: { name: 'Casas Bahia', color: '#FF6000', logo: '🏡' },
  carrefour: { name: 'Carrefour', color: '#005AA9', logo: '🛒' },
  leroy: { name: 'Leroy Merlin', color: '#78BE20', logo: '🏠' },
};

const STATUS_INFO = {
  pending: { label: 'Aguardando NF-e', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20', icon: Clock },
  invoiced: { label: 'Faturado', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20', icon: FileText },
  ready_to_ship: { label: 'Pronto p/ Envio', color: 'text-purple-400 bg-purple-400/10 border-purple-400/20', icon: Package },
  shipped: { label: 'Em Trânsito', color: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/20', icon: Truck },
  delivered: { label: 'Entregue', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: 'text-red-400 bg-red-400/10 border-red-400/20', icon: AlertCircle },
};

/** Mascara PII do comprador para exibição (conformidade Shopee DPP) */
function maskName(name: string): string {
  if (!name) return '—';
  return name
    .split(' ')
    .map(part => part.length <= 1 ? part : part[0] + '*'.repeat(Math.min(part.length - 1, 5)))
    .join(' ');
}

const REFRESH_INTERVAL_MS = 30_000; // 30 segundos

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [nfeOrder, setNfeOrder] = useState<Order | null>(null);
  const [fiscalOrder, setFiscalOrder] = useState<Order | null>(null);

  useEffect(() => {
    loadOrders();
  }, []);

  // Auto-refresh silencioso a cada 30 segundos (invisível ao usuário)
  useEffect(() => {
    const interval = setInterval(loadOrdersSilent, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/orders');
      setOrders(normalizeOrders(response.data.orders || []));
    } catch (error) {
      console.error('Falha ao carregar pedidos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Refresh silencioso: não mostra loading, só atualiza os dados
  const loadOrdersSilent = async () => {
    try {
      const response = await apiClient.get('/orders');
      setOrders(normalizeOrders(response.data.orders || []));
    } catch (error) {
      console.error('Falha ao atualizar pedidos:', error);
    }
  };

  const normalizeOrders = (raw: any[]): Order[] =>
    raw.map((o: any) => ({
      _id: o._id || o.id || o.orderNumber,
      orderId: o.orderId || o.orderNumber || o.order_id || '',
      marketplace: o.marketplaceId || o.marketplace || '',
      customerName: o.customerName || o.customer?.name || '',
      customerDocument: o.customerDocument || o.customer?.document || '',
      totalValue: o.totalValue ?? o.total ?? 0,
      status: toUiStatus(o.status, o.invoice),
      rawStatus: o.status || '',
      invoice: o.invoice,
      shipBy: o.shipBy || '',
      createdAt: o.createdAt || o.created_at || '',
      items: (o.items || []).map((i: any) => ({
        sku: i.sku || i.info?.sku || '',
        title: i.title || i.info?.name || i.name || '',
        quantity: i.quantity ?? 1,
        unitPrice: i.unitPrice || i.unit_price?.value || 0,
      })),
      shipping: o.shipping,
    }));

  const needsInvoice = (o: Order) =>
    o.status !== 'cancelled' && (!o.invoice || o.invoice.status === 'erro');

  const canPrintLabel = (o: Order) => {
    if (o.status === 'cancelled') return false;
    if (o.invoice && o.invoice.status !== 'erro') return true;
    return ['invoiced', 'ready_to_ship', 'shipped'].includes(o.status);
  };

  const handleImprimirEtiqueta = async (orderId: string) => {
    try {
      const response = await apiClient.get(`/orders/${orderId}/label`);

      alert('Etiqueta gerada pelo Marketplace! A iniciar impressão...');

      if (response.data.labelUrl) {
        window.open(response.data.labelUrl, '_blank');
      }
    } catch (error: any) {
      alert(`Não foi possível gerar etiqueta: ${error.response?.data?.error || error.message}`);
    }
  };

  const filteredOrders = orders.filter(order => {
    const safeOrderId = order.orderId || '';
    const safeCustomerName = order.customerName || '';

    const matchesSearch =
        safeOrderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        safeCustomerName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  return (
      <div className="p-8 space-y-6">
        {/* HEADER DA PÁGINA */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-dark-100 mb-2">Gestão de Pedidos</h1>
            <p className="text-dark-400">Acompanhe as vendas, emita notas e imprima etiquetas de todos os canais.</p>
          </div>
          <div className="flex gap-3">
            <button
                onClick={loadOrders}
                className="p-3 bg-dark-800 hover:bg-dark-700 text-dark-100 rounded-lg transition-colors border border-dark-600 flex items-center gap-2 shadow-lg"
            >
              <RefreshCw className={clsx("w-5 h-5", loading && "animate-spin")} />
              Sincronizar
            </button>
          </div>
        </div>

        {/* MÉTRICAS RÁPIDAS (DASHBOARD) */}
        <div className="grid grid-cols-4 gap-4">
          <div className="p-5 bg-dark-800 border border-dark-700 rounded-xl">
            <p className="text-sm font-medium text-dark-400 mb-1">Aguardando NF-e</p>
            <p className="text-2xl font-bold text-amber-400">{orders.filter(o => o.status === 'pending').length}</p>
          </div>
          <div className="p-5 bg-dark-800 border border-dark-700 rounded-xl">
            <p className="text-sm font-medium text-dark-400 mb-1">Prontos p/ Imprimir</p>
            <p className="text-2xl font-bold text-purple-400">{orders.filter(o => o.status === 'ready_to_ship' || o.status === 'invoiced').length}</p>
          </div>
          <div className="p-5 bg-dark-800 border border-dark-700 rounded-xl">
            <p className="text-sm font-medium text-dark-400 mb-1">Em Trânsito</p>
            <p className="text-2xl font-bold text-indigo-400">{orders.filter(o => o.status === 'shipped').length}</p>
          </div>
          <div className="p-5 bg-dark-800 border border-dark-700 rounded-xl">
            <p className="text-sm font-medium text-dark-400 mb-1">Entregues (Hoje)</p>
            <p className="text-2xl font-bold text-emerald-400">{orders.filter(o => o.status === 'delivered').length}</p>
          </div>
        </div>

        {/* ÁREA DE FILTROS */}
        <div className="bg-dark-900 border border-dark-700 rounded-xl p-4 flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-500 w-5 h-5" />
            <input
                type="text"
                placeholder="Buscar por ID do Pedido ou Nome do Cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-dark-100 focus:ring-2 focus:ring-primary-500 outline-none transition-all"
            />
          </div>
          <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-dark-800 border border-dark-600 rounded-lg text-dark-100 outline-none focus:ring-2 focus:ring-primary-500 min-w-[200px]"
          >
            <option value="all">Todos os Status</option>
            <option value="pending">Aguardando NF-e</option>
            <option value="invoiced">Faturados</option>
            <option value="ready_to_ship">Pronto p/ Envio</option>
            <option value="shipped">Em Trânsito</option>
            <option value="delivered">Entregues</option>
            <option value="cancelled">Cancelados</option>
          </select>
          <button className="px-4 py-2.5 bg-dark-800 hover:bg-dark-700 border border-dark-600 rounded-lg text-dark-100 flex items-center gap-2 transition-colors">
            <Filter className="w-5 h-5" /> Filtros Avançados
          </button>
        </div>

        {/* TABELA DE PEDIDOS */}
        <div className="bg-dark-900 border border-dark-700 rounded-xl overflow-hidden">
          {loading ? (
              <div className="p-16 flex flex-col items-center justify-center text-dark-400">
                <Loader className="w-10 h-10 animate-spin text-primary-500 mb-4" />
                <p>A carregar os seus pedidos...</p>
              </div>
          ) : filteredOrders.length === 0 ? (
              <div className="p-16 flex flex-col items-center justify-center text-dark-400 text-center">
                <Package className="w-12 h-12 mb-4 opacity-20" />
                <h3 className="text-lg font-medium text-dark-100 mb-1">Nenhum pedido encontrado</h3>
                <p>Não há pedidos que correspondam aos seus filtros no momento.</p>
              </div>
          ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                  <tr className="bg-dark-800/50 border-b border-dark-700 text-sm font-medium text-dark-400">
                    <th className="p-4 whitespace-nowrap">Canal / ID</th>
                    <th className="p-4">Cliente</th>
                    <th className="p-4">Data</th>
                    <th className="p-4">Status</th>
                    <th className="p-4 text-right">Valor Total</th>
                    <th className="p-4 text-center">Ações (OMS)</th>
                  </tr>
                  </thead>
                  <tbody className="divide-y divide-dark-700">
                  {filteredOrders.map((order) => {
                    const mp = MARKETPLACES_INFO[order.marketplace] || { name: order.marketplace, color: '#666', logo: '🛒' };
                    const status = STATUS_INFO[order.status] || STATUS_INFO.pending;
                    const StatusIcon = status.icon;

                    return (
                        <tr key={order._id} className="hover:bg-dark-800/20 transition-colors group">

                          {/* Marketplace & Order ID */}
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div
                                  className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                                  style={{ backgroundColor: `${mp.color}20`, border: `1px solid ${mp.color}40` }}
                                  title={mp.name}
                              >
                                {mp.logo}
                              </div>
                              <div>
                                <p className="font-mono text-sm font-medium text-dark-100">{order.orderId}</p>
                                <p className="text-xs text-dark-400">{mp.name}</p>
                              </div>
                            </div>
                          </td>

                          {/* Cliente e Itens */}
                          <td className="p-4">
                            <p className="font-medium text-dark-100">{maskName(order.customerName)}</p>
                            <p className="text-xs text-dark-400 mt-0.5 truncate max-w-[200px]" title={order.items.map(i => `${i.quantity}x ${i.title}`).join(', ')}>
                              {order.items.length} item(s) • {order.items[0]?.title}
                            </p>
                          </td>

                          {/* Data */}
                          <td className="p-4 text-sm text-dark-300">
                            {new Date(order.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            {order.shipBy && !['shipped', 'delivered', 'cancelled'].includes(order.status) && (
                              <ShipByBadge date={order.shipBy} />
                            )}
                          </td>

                          {/* Status */}
                          <td className="p-4">
                            <div className={clsx("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium", status.color)}>
                              <StatusIcon className="w-3.5 h-3.5" />
                              {status.label}
                            </div>
                          </td>

                          {/* Valor */}
                          <td className="p-4 text-right">
                            <p className="font-semibold text-emerald-400">
                              R$ {(order.totalValue || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </p>
                          </td>

                          {/* Ações / Botões OMS */}
                          <td className="p-4">
                            <div className="flex items-center justify-center gap-2 opacity-100 lg:opacity-60 lg:group-hover:opacity-100 transition-opacity">

                              {/* Dados para emitir NF-e */}
                              <button
                                  onClick={() => setFiscalOrder(order)}
                                  className="p-2 rounded-lg transition-all flex items-center gap-2 text-sm font-medium border bg-dark-800 text-dark-300 border-dark-600 hover:text-dark-100 hover:border-primary-500/50"
                                  title="Dados do comprador, itens e valores para emitir a NF-e"
                              >
                                <ClipboardList className="w-4 h-4" />
                                <span className="hidden xl:inline">Dados</span>
                              </button>

                              {/* Botão de NF-e */}
                              <button
                                  onClick={() => setNfeOrder(order)}
                                  disabled={order.status === 'cancelled'}
                                  className={clsx(
                                      "p-2 rounded-lg transition-all flex items-center gap-2 text-sm font-medium border",
                                      order.status === 'cancelled'
                                          ? "bg-dark-800/50 text-dark-600 border-dark-700 cursor-not-allowed"
                                          : needsInvoice(order)
                                          ? "bg-primary-500/10 text-primary-400 border-primary-500/30 hover:bg-primary-500 hover:text-dark-100"
                                          : "bg-dark-800 text-dark-400 border-dark-600 hover:text-dark-100"
                                  )}
                                  title={
                                    order.status === 'cancelled' ? 'Pedido cancelado'
                                    : order.invoice?.status === 'erro' ? `NF-e recusada: ${order.invoice.message || ''} — clique para reprocessar o envio`
                                    : needsInvoice(order) ? 'Enviar NF-e (XML) para o marketplace'
                                    : `Ver NF-e ${order.invoice?.number || ''}`
                                  }
                              >
                                <FileText className="w-4 h-4" />
                                <span className="hidden xl:inline">
                                  {order.invoice?.status === 'erro' ? 'Reenviar NF' : needsInvoice(order) ? 'Faturar' : `NF ${order.invoice?.number || ''}`}
                                </span>
                                {order.invoice && (
                                  <span className={clsx("w-2 h-2 rounded-full",
                                    order.invoice.status === 'erro' ? 'bg-red-400'
                                    : order.invoice.status === 'enviando' ? 'bg-amber-400'
                                    : 'bg-emerald-400')} />
                                )}
                              </button>

                              {/* Botão de Etiqueta */}
                              <button
                                  onClick={() => handleImprimirEtiqueta(order.orderId)}
                                  disabled={!canPrintLabel(order)}
                                  className={clsx(
                                      "p-2 rounded-lg transition-all flex items-center gap-2 text-sm font-medium border",
                                      canPrintLabel(order)
                                          ? "bg-indigo-500/10 text-indigo-400 border-indigo-500/30 hover:bg-indigo-500 hover:text-dark-100"
                                          : "bg-dark-800/50 text-dark-600 border-dark-700 cursor-not-allowed"
                                  )}
                                  title={canPrintLabel(order) ? "Imprimir Etiqueta" : "Envie a NF-e primeiro para liberar a etiqueta"}
                              >
                                <Printer className="w-4 h-4" />
                                <span className="hidden xl:inline">Etiqueta</span>
                              </button>

                            </div>
                          </td>

                        </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
          )}
        </div>

        {nfeOrder && (
          <InvoiceModal
            order={nfeOrder}
            onClose={() => setNfeOrder(null)}
            onSent={() => { loadOrdersSilent(); }}
            onShowFiscal={() => { setFiscalOrder(nfeOrder); setNfeOrder(null); }}
          />
        )}

        {fiscalOrder && (
          <FiscalDrawer
            order={fiscalOrder}
            onClose={() => setFiscalOrder(null)}
            onUploadNfe={() => { setNfeOrder(fiscalOrder); setFiscalOrder(null); }}
          />
        )}
      </div>
  );
}

/* ───────────────────────── Modal de NF-e ───────────────────────── */

function InvoiceModal({ order, onClose, onSent, onShowFiscal }: { order: Order; onClose: () => void; onSent: () => void; onShowFiscal: () => void }) {
  const mpName = MARKETPLACES_INFO[order.marketplace]?.name || order.marketplace;
  const hasInvoice = !!order.invoice;
  const [mode, setMode] = useState<'view' | 'upload'>(hasInvoice ? 'view' : 'upload');
  const [history, setHistory] = useState<NfeHistory[] | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [invStatus, setInvStatus] = useState<OrderInvoice['status'] | undefined>(order.invoice?.status);
  const [invMessage, setInvMessage] = useState(order.invoice?.message || '');

  const loadHistory = () =>
    apiClient.get(`/orders/${order.orderId}/invoice/history`)
      .then(r => {
        const notas: NfeHistory[] = r.data?.notas || [];
        setHistory(notas);
        const ativa = notas.find(n => n.ativa);
        if (ativa?.status) { setInvStatus(ativa.status as OrderInvoice['status']); setInvMessage(ativa.mensagem || ''); }
      })
      .catch(() => setHistory([]));

  useEffect(() => { if (hasInvoice) loadHistory(); /* eslint-disable-next-line */ }, [order.orderId]);

  // Enquanto o envio está em andamento, atualiza o histórico a cada 5 s
  useEffect(() => {
    if (mode !== 'view' || invStatus !== 'enviando') return;
    const t = setInterval(loadHistory, 5000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [mode, invStatus]);

  const retry = async () => {
    setRetrying(true); setError('');
    try {
      const r = await apiClient.post(`/orders/${order.orderId}/invoice/retry`, {});
      setInvStatus('enviando'); setInvMessage('');
      setResult(r.data?.message || 'Reenvio colocado na fila.');
      onSent();
      setTimeout(loadHistory, 1500);
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Falha ao reprocessar.');
    } finally {
      setRetrying(false);
    }
  };

  const downloadXml = async () => {
    try {
      const r = await apiClient.get(`/orders/${order.orderId}/invoice/xml`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `NFe${order.invoice?.key || order.orderId}.xml`; a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError('Não foi possível baixar o XML.');
    }
  };
  const [xml, setXml] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<ReturnType<typeof previewNfe>>(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState('');
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !sending && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sending, onClose]);

  const readFile = (file?: File) => {
    setError(''); setResult('');
    if (!file) return;
    if (!/\.xml$/i.test(file.name)) { setError('Selecione o arquivo .xml da NF-e (não o PDF/DANFE).'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('Arquivo maior que 2 MB.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const p = previewNfe(text);
      if (!p || p.key.length !== 44) {
        setError('Não reconhecemos este arquivo como XML de NF-e. Use o XML autorizado baixado do seu emissor.');
        setXml(''); setPreview(null); return;
      }
      setXml(text); setPreview(p); setFileName(file.name);
    };
    reader.readAsText(file, 'utf-8');
  };

  const send = async () => {
    if (!xml) return;
    setSending(true); setError('');
    try {
      const res = await apiClient.post(`/orders/${order.orderId}/invoice`, { xml });
      setResult(res.data?.message || 'NF-e enviada.');
      onSent();
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Falha ao enviar a NF-e.');
    } finally {
      setSending(false);
    }
  };

  const valueDiff = preview && order.totalValue
    ? Math.abs(preview.amount - order.totalValue) / order.totalValue > 0.3
    : false;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !sending && onClose()}>
      <div className="w-full max-w-lg bg-dark-900 border border-dark-700 rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-dark-700 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-dark-100">
              {mode === 'view' ? 'Nota fiscal do pedido' : 'Enviar NF-e'}
            </h2>
            <p className="text-sm text-dark-400 mt-0.5">
              Pedido <span className="font-mono text-dark-200">{order.orderId}</span> · {mpName} · {brl(order.totalValue)}
            </p>
          </div>
          <button onClick={onClose} disabled={sending} className="text-dark-400 hover:text-dark-100 text-2xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Visualização da NF já enviada */}
          {mode === 'view' && order.invoice && (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Info label="Número / Série" value={`${order.invoice.number || '—'} / ${order.invoice.series || '—'}`} />
                <Info label="Valor" value={brl(order.invoice.amount || 0)} />
                <Info label="Emissão" value={order.invoice.issuedAt ? new Date(order.invoice.issuedAt).toLocaleString('pt-BR') : '—'} />
                <Info label="Status no marketplace"
                      value={invStatus ? INVOICE_BADGE[invStatus]?.label : '—'}
                      valueClass={invStatus ? INVOICE_BADGE[invStatus]?.cls : ''} />
              </div>
              <div>
                <p className="text-xs text-dark-400 mb-1">Chave de acesso</p>
                <p className="font-mono text-xs text-dark-200 bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 break-all">{order.invoice.key}</p>
              </div>
              {invMessage && (
                <p className={clsx("text-sm rounded-lg px-3 py-2 border",
                  invStatus === 'erro' ? "text-red-300 bg-red-500/10 border-red-500/30" : "text-dark-300 bg-dark-800 border-dark-700")}>
                  {invMessage}
                </p>
              )}

              {/* Histórico de envios */}
              <div>
                <p className="text-xs text-dark-400 mb-2">Histórico de envios ao marketplace</p>
                {history === null ? (
                  <p className="text-xs text-dark-500 flex items-center gap-2"><Loader className="w-3.5 h-3.5 animate-spin" /> Carregando…</p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {(history.find(n => n.ativa)?.tentativas || []).slice().reverse().map((t, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs bg-dark-800/60 border border-dark-700 rounded-lg px-3 py-2">
                        <span className={clsx("mt-1 w-2 h-2 rounded-full flex-shrink-0",
                          t.status === 'erro' ? 'bg-red-400' : t.status === 'enviando' ? 'bg-amber-400' : 'bg-emerald-400')} />
                        <div className="min-w-0">
                          <p className="text-dark-200">
                            {t.em ? new Date(t.em + (t.em.endsWith('Z') ? '' : 'Z')).toLocaleString('pt-BR') : '—'}
                            <span className="text-dark-500"> · {t.origem === 'reprocessamento' ? 'reenvio' : 'envio do XML'}</span>
                            <span className={clsx('ml-1', t.status ? INVOICE_BADGE[t.status as OrderInvoice['status']]?.cls : '')}>
                              · {t.status ? INVOICE_BADGE[t.status as OrderInvoice['status']]?.label : ''}
                            </span>
                          </p>
                          {t.mensagem && <p className="text-dark-400 break-words">{t.mensagem}</p>}
                        </div>
                      </div>
                    ))}
                    {(history.find(n => n.ativa)?.tentativas || []).length === 0 && (
                      <p className="text-xs text-dark-500">Nenhum envio registrado.</p>
                    )}
                    {history.filter(n => !n.ativa).length > 0 && (
                      <p className="text-[11px] text-dark-500 pt-1">
                        XML substituído antes: {history.filter(n => !n.ativa).map(n => `NF ${n.number}`).join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Upload */}
          {mode === 'upload' && !result && (
            <>
              {order.invoice?.status === 'erro' && (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                  A NF {order.invoice.number} foi recusada: {order.invoice.message || 'sem detalhes'}. Se o problema foi na nota, envie o XML corrigido.
                </div>
              )}
              <label
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
                className={clsx(
                  "flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-8 cursor-pointer transition-colors text-center",
                  dragOver ? "border-primary-500 bg-primary-500/10" : "border-dark-600 hover:border-primary-500/60 bg-dark-800/40"
                )}
              >
                <FileText className="w-8 h-8 text-primary-400" />
                <span className="text-sm font-medium text-dark-100">
                  {fileName || 'Arraste o XML da NF-e aqui ou clique para selecionar'}
                </span>
                <span className="text-xs text-dark-400">Arquivo .xml autorizado pela SEFAZ (máx. 2 MB)</span>
                <input type="file" accept=".xml,text/xml,application/xml" className="hidden"
                       onChange={e => readFile(e.target.files?.[0])} />
              </label>
              {!preview && (
                <button onClick={onShowFiscal} className="w-full text-sm text-primary-400 hover:text-primary-300 flex items-center justify-center gap-1.5">
                  <ClipboardList className="w-4 h-4" /> Ainda não emitiu? Ver dados do comprador para emitir a nota
                </button>
              )}

              {preview && (
                <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-dark-400">NF-e</span><span className="text-dark-100 font-medium">{preview.number} / série {preview.series}</span></div>
                  <div className="flex justify-between"><span className="text-dark-400">Valor</span><span className="text-dark-100 font-medium">{brl(preview.amount)}</span></div>
                  <div className="flex justify-between"><span className="text-dark-400">Emitente</span><span className="text-dark-100 truncate max-w-[60%] text-right">{preview.emitente || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-dark-400">Destinatário</span><span className="text-dark-100 truncate max-w-[60%] text-right">{preview.destinatario || '—'}</span></div>
                  <p className="font-mono text-[11px] text-dark-400 break-all pt-1">{preview.key}</p>
                  {!preview.authorized && (
                    <p className="text-xs text-amber-300">⚠️ Este XML não traz o protocolo de autorização da SEFAZ. Use o XML final (autorizado) para evitar recusa.</p>
                  )}
                  {valueDiff && (
                    <p className="text-xs text-amber-300">⚠️ O valor da nota é bem diferente do valor do pedido ({brl(order.totalValue)}). Confira se é a nota certa.</p>
                  )}
                </div>
              )}
            </>
          )}

          {result && mode === 'view' && (
            <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">🔄 {result}</div>
          )}
          {result && mode === 'upload' && (
            <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-3">
              ✅ {result}<br />
              <span className="text-dark-300">O status atualiza nesta tela em alguns segundos. A etiqueta é liberada quando o marketplace aprovar a nota.</span>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-700 flex justify-between gap-3">
          {mode === 'view' ? (
            <>
              <div className="flex gap-2">
                <button onClick={() => { setResult(''); setMode('upload'); }} className="px-3 py-2 text-sm rounded-lg border border-dark-600 text-dark-300 hover:text-dark-100 hover:bg-dark-800">
                  Substituir XML
                </button>
                <button onClick={downloadXml} className="px-3 py-2 text-sm rounded-lg border border-dark-600 text-dark-300 hover:text-dark-100 hover:bg-dark-800">
                  Baixar XML
                </button>
              </div>
              {invStatus === 'erro' || invStatus === 'enviando' ? (
                <button onClick={retry} disabled={retrying}
                        title="Reenvia ao marketplace a mesma NF-e já salva"
                        className={clsx("px-5 py-2 text-sm rounded-lg font-medium flex items-center gap-2",
                          retrying ? "bg-dark-700 text-dark-500" : "bg-primary-500 text-white hover:bg-primary-600")}>
                  {retrying ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {retrying ? 'Reenviando…' : `Reenviar para ${mpName}`}
                </button>
              ) : (
                <button onClick={onClose} className="px-5 py-2 text-sm rounded-lg bg-dark-700 text-dark-100 hover:bg-dark-600">Fechar</button>
              )}
            </>
          ) : result && mode === 'upload' ? (
            <button onClick={onClose} className="ml-auto px-5 py-2 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-600">Concluir</button>
          ) : (
            <>
              <button onClick={() => (hasInvoice ? setMode('view') : onClose())} disabled={sending}
                      className="px-4 py-2 text-sm rounded-lg border border-dark-600 text-dark-300 hover:text-dark-100 hover:bg-dark-800">
                {hasInvoice ? 'Voltar' : 'Cancelar'}
              </button>
              <button onClick={send} disabled={!xml || sending}
                      className={clsx("px-5 py-2 text-sm rounded-lg font-medium flex items-center gap-2",
                        xml && !sending ? "bg-primary-500 text-white hover:bg-primary-600" : "bg-dark-700 text-dark-500 cursor-not-allowed")}>
                {sending && <Loader className="w-4 h-4 animate-spin" />}
                {sending ? 'Enviando…' : `Enviar para ${mpName}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Prazo de postagem ───────────────────────── */

function ShipByBadge({ date }: { date: string }) {
  // limit_date vem em UTC (YYYY-MM-DD); comparamos só a data, no fuso local
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  if (!y) return null;
  const limit = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((limit.getTime() - today.getTime()) / 86_400_000);
  const label = days < 0 ? `Atrasado (${limit.toLocaleDateString('pt-BR')})`
    : days === 0 ? 'Postar hoje'
    : days === 1 ? 'Postar até amanhã'
    : `Postar até ${limit.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
  const cls = days < 0 ? 'text-red-400' : days <= 1 ? 'text-amber-400' : 'text-dark-400';
  return (
    <p className={clsx('text-xs mt-1 flex items-center gap-1 font-medium', cls)}>
      <CalendarClock className="w-3.5 h-3.5" /> {label}
    </p>
  );
}

/* ───────────────────────── Dados para emissão da NF-e ───────────────────────── */

interface FiscalAddress {
  cep: string; logradouro: string; numero: string; complemento: string;
  bairro: string; cidade: string; uf: string; pais: string; referencia: string;
}
interface FiscalData {
  orderNumber: string;
  marketplace: string;
  marketplaceNome: string;
  compradoEm: string;
  intermediador?: { cnpj: string; nome: string; identificador: string } | null;
  destinatario: { nome: string; documento: string; tipoDocumento: string; email: string; telefone: string; endereco: FiscalAddress | null };
  itens: { sku: string; titulo: string; ean: string; quantidade: number; valorUnitario: number; valorTotal: number }[];
  totais: { produtos: number; frete: number; desconto: number; total: number };
  entrega: {
    modalidade?: string; magaluEntregas?: boolean; fulfillment?: boolean; prazoPostagem?: string;
    entregaCodigo?: string;
    pontoPostagem?: { cnpj: string; endereco: FiscalAddress } | null;
  };
  aviso?: string;
}

const fmtDoc = (doc: string) => {
  const d = (doc || '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc || '';
};
const fmtCep = (cep: string) => (cep || '').replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2');
const addrOneLine = (a: FiscalAddress) =>
  [`${a.logradouro}${a.numero ? ', ' + a.numero : ''}`, a.complemento, a.bairro, `${a.cidade}/${a.uf}`, fmtCep(a.cep)]
    .filter(Boolean).join(' - ');

function CopyField({ label, value, display, mono }: { label: string; value: string; display?: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (!value) return (
    <div className="py-1.5">
      <p className="text-[11px] uppercase tracking-wide text-dark-500">{label}</p>
      <p className="text-sm text-dark-500">—</p>
    </div>
  );
  return (
    <button
      type="button"
      onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      className="group w-full text-left py-1.5 rounded-md hover:bg-dark-800/60 px-2 -mx-2 transition-colors"
      title="Clique para copiar"
    >
      <p className="text-[11px] uppercase tracking-wide text-dark-500">{label}</p>
      <p className={clsx('text-sm text-dark-100 flex items-center gap-2 break-all', mono && 'font-mono')}>
        <span>{display ?? value}</span>
        {copied
          ? <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
          : <Copy className="w-3.5 h-3.5 text-dark-500 opacity-0 group-hover:opacity-100 flex-shrink-0" />}
      </p>
    </button>
  );
}

function Section({ icon: Icon, title, children, action }: { icon: any; title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="bg-dark-800/40 border border-dark-700 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-dark-100 flex items-center gap-2">
          <Icon className="w-4 h-4 text-primary-400" /> {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function FiscalDrawer({ order, onClose, onUploadNfe }: { order: Order; onClose: () => void; onUploadNfe: () => void }) {
  const [data, setData] = useState<FiscalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copiedAll, setCopiedAll] = useState(false);

  useEffect(() => {
    let alive = true;
    apiClient.get(`/orders/${order.orderId}/fiscal`)
      .then(r => alive && setData(r.data))
      .catch(e => alive && setError(e.response?.data?.error || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [order.orderId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copyAll = () => {
    if (!data) return;
    const d = data.destinatario, e = d.endereco;
    const linhas = [
      `PEDIDO ${data.marketplaceNome} #${data.orderNumber}`,
      '',
      'DESTINATÁRIO',
      `Nome: ${d.nome}`,
      `${d.tipoDocumento || 'CPF/CNPJ'}: ${fmtDoc(d.documento)}`,
      e ? `Endereço: ${e.logradouro}, ${e.numero}${e.complemento ? ' - ' + e.complemento : ''}` : '',
      e ? `Bairro: ${e.bairro}` : '',
      e ? `Cidade/UF: ${e.cidade}/${e.uf}` : '',
      e ? `CEP: ${fmtCep(e.cep)}` : '',
      d.email ? `E-mail: ${d.email}` : '',
      d.telefone ? `Telefone: ${d.telefone}` : '',
      '',
      'ITENS',
      ...data.itens.map(i => `${i.quantidade}x ${i.sku} - ${i.titulo} - ${brl(i.valorUnitario)} un. = ${brl(i.valorTotal)}`),
      '',
      `Produtos: ${brl(data.totais.produtos)} | Frete: ${brl(data.totais.frete)} | Desconto: ${brl(data.totais.desconto)} | Total: ${brl(data.totais.total)}`,
      data.intermediador ? `\nINTERMEDIADOR: ${data.intermediador.nome} - CNPJ ${fmtDoc(data.intermediador.cnpj)} - Identificador: ${data.intermediador.identificador}` : '',
    ].filter(l => l !== '').join('\n');
    navigator.clipboard.writeText(linhas);
    setCopiedAll(true); setTimeout(() => setCopiedAll(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <aside className="w-full max-w-xl h-full bg-dark-900 border-l border-dark-700 shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-5 border-b border-dark-700 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-dark-100">Dados para emitir a NF-e</h2>
            <p className="text-sm text-dark-400 mt-0.5">
              Pedido <span className="font-mono text-dark-200">{order.orderId}</span> · {MARKETPLACES_INFO[order.marketplace]?.name || order.marketplace}
            </p>
          </div>
          <button onClick={onClose} className="text-dark-400 hover:text-dark-100 text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-16 text-dark-400">
              <Loader className="w-6 h-6 animate-spin mr-2" /> Buscando dados do pedido…
            </div>
          )}
          {error && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{error}</div>}

          {data && (
            <>
              {data.aviso && (
                <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">⚠️ {data.aviso}</div>
              )}

              {/* Prazo */}
              {data.entrega?.prazoPostagem && (
                <div className="flex items-center justify-between bg-primary-500/10 border border-primary-500/30 rounded-xl px-4 py-3">
                  <div>
                    <p className="text-xs text-primary-300 uppercase tracking-wide font-semibold">Prazo de postagem</p>
                    <p className="text-dark-100 font-bold">{new Date(data.entrega.prazoPostagem.slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</p>
                  </div>
                  <ShipByBadge date={data.entrega.prazoPostagem} />
                </div>
              )}

              {/* Destinatário */}
              <Section icon={User} title="Destinatário">
                <div className="grid grid-cols-2 gap-x-4">
                  <div className="col-span-2"><CopyField label="Nome / Razão social" value={data.destinatario.nome} /></div>
                  <CopyField label={data.destinatario.tipoDocumento || 'CPF / CNPJ'} value={(data.destinatario.documento || '').replace(/\D/g, '')}
                             display={fmtDoc(data.destinatario.documento)} mono />
                  <CopyField label="Telefone" value={data.destinatario.telefone} />
                  {data.destinatario.email && <div className="col-span-2"><CopyField label="E-mail" value={data.destinatario.email} /></div>}
                </div>
                {data.destinatario.endereco && (
                  <div className="grid grid-cols-6 gap-x-4 mt-2 pt-2 border-t border-dark-700">
                    <div className="col-span-4"><CopyField label="Logradouro" value={data.destinatario.endereco.logradouro} /></div>
                    <div className="col-span-2"><CopyField label="Número" value={data.destinatario.endereco.numero} /></div>
                    <div className="col-span-3"><CopyField label="Complemento" value={data.destinatario.endereco.complemento} /></div>
                    <div className="col-span-3"><CopyField label="Bairro" value={data.destinatario.endereco.bairro} /></div>
                    <div className="col-span-3"><CopyField label="Cidade" value={data.destinatario.endereco.cidade} /></div>
                    <div className="col-span-1"><CopyField label="UF" value={data.destinatario.endereco.uf} /></div>
                    <div className="col-span-2"><CopyField label="CEP" value={(data.destinatario.endereco.cep || '').replace(/\D/g, '')} display={fmtCep(data.destinatario.endereco.cep)} mono /></div>
                    {data.destinatario.endereco.referencia && (
                      <div className="col-span-6"><CopyField label="Referência" value={data.destinatario.endereco.referencia} /></div>
                    )}
                  </div>
                )}
              </Section>

              {/* Itens */}
              <Section icon={Package} title={`Itens (${data.itens.length})`}>
                <div className="divide-y divide-dark-700">
                  {data.itens.map((i, idx) => (
                    <div key={idx} className="py-2.5 flex justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm text-dark-100 truncate" title={i.titulo}>{i.titulo}</p>
                        <p className="text-xs text-dark-400 font-mono mt-0.5">
                          SKU {i.sku || '—'}{i.ean ? ` · EAN ${i.ean}` : ''}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm text-dark-100">{i.quantidade} × {brl(i.valorUnitario)}</p>
                        <p className="text-xs text-dark-400">{brl(i.valorTotal)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-3 border-t border-dark-700 space-y-1 text-sm">
                  <div className="flex justify-between text-dark-300"><span>Produtos</span><span>{brl(data.totais.produtos)}</span></div>
                  <div className="flex justify-between text-dark-300"><span>Frete</span><span>{brl(data.totais.frete)}</span></div>
                  {data.totais.desconto > 0 && (
                    <div className="flex justify-between text-dark-300"><span>Desconto</span><span>− {brl(data.totais.desconto)}</span></div>
                  )}
                  <div className="flex justify-between text-dark-100 font-bold pt-1"><span>Total do pedido</span><span>{brl(data.totais.total)}</span></div>
                </div>
              </Section>

              {/* Intermediador */}
              {data.intermediador && (
                <Section icon={Store} title="Intermediador da operação">
                  <p className="text-xs text-dark-400 mb-1">Na NF-e, marque "Operação com intermediador" e informe:</p>
                  <div className="grid grid-cols-2 gap-x-4">
                    <div className="col-span-2"><CopyField label="Razão social" value={data.intermediador.nome} /></div>
                    <CopyField label="CNPJ" value={data.intermediador.cnpj} display={fmtDoc(data.intermediador.cnpj)} mono />
                    <CopyField label="Identificador no intermediador" value={data.intermediador.identificador} />
                  </div>
                </Section>
              )}

              {/* Entrega */}
              {(data.entrega?.modalidade || data.entrega?.pontoPostagem) && (
                <Section icon={MapPin} title="Entrega">
                  <div className="grid grid-cols-2 gap-x-4">
                    <CopyField label="Modalidade" value={data.entrega.modalidade || ''} />
                    {data.entrega.entregaCodigo && <CopyField label="Código da entrega" value={data.entrega.entregaCodigo} mono />}
                  </div>
                  {data.entrega.fulfillment && (
                    <p className="text-xs text-emerald-300 mt-1">Pedido Fulfillment — a NF-e é emitida pelo CD da Magalu.</p>
                  )}
                  {data.entrega.pontoPostagem && (
                    <div className="mt-2 pt-2 border-t border-dark-700">
                      <p className="text-xs text-dark-400 mb-1">Ponto de postagem (onde deixar o pacote)</p>
                      <CopyField label="Endereço" value={addrOneLine(data.entrega.pontoPostagem.endereco)} />
                      <CopyField label="CNPJ da loja" value={data.entrega.pontoPostagem.cnpj} display={fmtDoc(data.entrega.pontoPostagem.cnpj)} mono />
                    </div>
                  )}
                </Section>
              )}

              <p className="text-xs text-dark-500 leading-relaxed">
                🔒 Dados pessoais do comprador. Use somente para emissão da nota e envio do pedido (LGPD).
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-700 flex gap-3">
          <button onClick={copyAll} disabled={!data}
                  className="flex-1 px-4 py-2.5 text-sm rounded-lg border border-dark-600 text-dark-200 hover:bg-dark-800 flex items-center justify-center gap-2 disabled:opacity-40">
            {copiedAll ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            {copiedAll ? 'Copiado!' : 'Copiar tudo'}
          </button>
          {order.status !== 'cancelled' && (
            <button onClick={onUploadNfe}
                    className="flex-1 px-4 py-2.5 text-sm rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium flex items-center justify-center gap-2">
              <FileText className="w-4 h-4" /> {order.invoice && order.invoice.status !== 'erro' ? 'Ver NF-e enviada' : 'Já emiti — enviar XML'}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

function Info({ label, value, valueClass }: { label: string; value?: string; valueClass?: string }) {
  return (
    <div className="bg-dark-800 border border-dark-700 rounded-lg px-3 py-2">
      <p className="text-xs text-dark-400">{label}</p>
      <p className={clsx("text-dark-100 font-medium mt-0.5", valueClass)}>{value || '—'}</p>
    </div>
  );
}
